import React from "react";
import { Users, Eye, TrendingUp, Heart } from "lucide-react";

import { InfluencerDB } from "@/hooks/useInfluencers";

interface InfluencerProfile {
  followers_count?: number | null;
  followers?: {
    instagram?: string | null;
    youtube?: string | null;
  };
  engagementRate: number;
  _db?: Partial<InfluencerDB> | null;
}

interface Props {
  influencer: InfluencerProfile;
}

export default function InfluencerKeyMetrics({ influencer }: Props) {
  if (!influencer) return null;

  const formatFollowers = (count?: number | null) => {
    if (!count) return "0";
    if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + "M";
    if (count >= 1_000) return (count / 1_000).toFixed(0) + "K";
    return count.toString();
  };

  const formatViews = () => {
    const views = influencer._db?.video_view_count;
    return views ? formatFollowers(views) : "-";
  };

  const calcAvgLikes = () => {
    const avgLikes = influencer._db?.avg_likes;
    return avgLikes ? formatFollowers(avgLikes) : "-";
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
      {/* Item 1: Followers */}
      <div className="flex items-center gap-4 flex-1 w-full justify-start">
        <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 flex-shrink-0">
          <Users className="w-6 h-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">
            {formatFollowers(influencer._db?.followers_count ?? influencer.followers_count)}
          </div>
          <div className="text-sm text-slate-500 font-medium">Followers</div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden md:block h-12 w-px bg-slate-200" />

      {/* Item 2: Average Views */}
      <div className="flex items-center gap-4 flex-1 w-full justify-start">
        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-500 flex-shrink-0">
          <Eye className="w-6 h-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">
            {formatViews()}
          </div>
          <div className="text-sm text-slate-500 font-medium">Average Views</div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden md:block h-12 w-px bg-slate-200" />

      {/* Item 3: Engagement Rate */}
      <div className="flex items-center gap-4 flex-1 w-full justify-start">
        <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center text-green-500 flex-shrink-0">
          <TrendingUp className="w-6 h-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">
            {influencer.engagementRate ? `${influencer.engagementRate.toFixed(2)}%` : "—"}
          </div>
          <div className="text-sm text-slate-500 font-medium">Engagement Rate</div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden md:block h-12 w-px bg-slate-200" />

      {/* Item 4: Avg. Likes */}
      <div className="flex items-center gap-4 flex-1 w-full justify-start">
        <div className="w-12 h-12 bg-purple-50 rounded-2xl flex items-center justify-center text-purple-500 flex-shrink-0">
          <Heart className="w-6 h-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">
            {calcAvgLikes()}
          </div>
          <div className="text-sm text-slate-500 font-medium">Avg. Likes</div>
        </div>
      </div>
    </div>
  );
}
